import React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  type DeliveryCarrier,
  type DeliveryPricingResponse,
  getAvailableDeliveryCarriers,
  getDeliveryCarrierLabel,
  getDeliveryCarrierPriceHint,
  getDeliveryCarrierShortDescription,
} from '../utils/deliveryPricing';
import DeliveryCarrierLogo from './DeliveryCarrierLogo';

type Props = {
  value: DeliveryCarrier | null;
  onChange: (carrier: DeliveryCarrier) => void;
  pricing: DeliveryPricingResponse | null | undefined;
  locationProviderId?: number;
  disabled?: boolean;
};

export default function ChooseDeliveryCarrierPanel({
  value,
  onChange,
  pricing,
  locationProviderId,
  disabled = false,
}: Props) {
  const availableCarriers = getAvailableDeliveryCarriers(pricing);

  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, v: DeliveryCarrier | null) => v && onChange(v)}
      disabled={disabled}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
        '& .MuiToggleButton-root': {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          textTransform: 'none',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'left',
          gap: 0.5,
          p: 2,
        },
        '& .MuiToggleButton-root.Mui-selected': {
          borderColor: 'primary.main',
          backgroundColor: 'primary.50',
        },
      }}
    >
      {availableCarriers.map((carrier) => (
        <ToggleButton key={carrier} value={carrier}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
            <DeliveryCarrierLogo
              carrier={carrier}
              height={28}
              maxWidth={108}
              pricing={pricing}
              locationProviderId={locationProviderId}
            />
            <Typography fontWeight={700}>{getDeliveryCarrierLabel(carrier)}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {getDeliveryCarrierShortDescription(carrier)} — {getDeliveryCarrierPriceHint(carrier, pricing)}
          </Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
